import { useState, useEffect, useMemo } from 'react';
import IconRenderer from './ui/IconRenderer';
import { X, Calendar, Clock, Tag, Flag, AlertCircle } from 'lucide-react';
import useProjectStore from '../stores/useProjectStore';
import useIdentityStore from '../stores/useIdentityStore';
import useTaskStore from '../stores/useTaskStore';
import CustomDatePicker from './ui/CustomDatePicker';
import CustomSelect from './ui/CustomSelect';
import TagSelector from './ui/TagSelector';
import CustomDuration from './ui/CustomDuration';
import DualModeEditor, { getPlainText } from './ui/DualModeEditor';
import './TaskModal.css';

const PRIORITY_COLORS = { low: 'var(--text-secondary)', medium: 'var(--caution)', high: 'var(--error)' };

export default function TaskModal({ task, onSave, onClose }) {
    const projects = useProjectStore(s => s.projects);
    const identities = useIdentityStore(s => s.identities);

    const [title, setTitle] = useState(task?.title || '');
    const [description, setDescription] = useState(task?.description || '');
    const [priority, setPriority] = useState(task?.priority || 'medium');
    const [status, setStatus] = useState(task?.status || 'todo');
    const [dueDate, setDueDate] = useState(task?.dueDate || '');
    const [estimatedMinutes, setEstimatedMinutes] = useState(task?.estimatedMinutes || '');
    const [projectId, setProjectId] = useState(task?.projectId || '');
    const [phaseId, setPhaseId] = useState(task?.phaseId || '');
    const [tags, setTags] = useState(task?.tags || []);

    // Get phases for the selected project
    const selectedProject = useMemo(() =>
        projects.find(p => p.id === projectId), [projects, projectId]
    );

    const phaseOptions = useMemo(() => {
        if (!selectedProject?.phases?.length) return [];
        return selectedProject.phases
            .sort((a, b) => a.order - b.order)
            .map(p => ({ value: p.id, label: p.name }));
    }, [selectedProject]);

    // When project changes, reset phaseId to first phase
    useEffect(() => {
        if (projectId && selectedProject?.phases?.length) {
            const sorted = [...selectedProject.phases].sort((a, b) => a.order - b.order);
            // Keep current phaseId if it belongs to this project
            const valid = sorted.some(p => p.id === phaseId);
            if (!valid) setPhaseId(sorted[0]?.id || '');
        } else {
            setPhaseId('');
        }
    }, [projectId]);

    const addTask = useTaskStore(s => s.addTask);
    const updateTask = useTaskStore(s => s.updateTask);
    const completeTask = useTaskStore(s => s.completeTask);
    const uncompleteTask = useTaskStore(s => s.uncompleteTask);

    const handleSave = () => {
        if (!title.trim()) return;
        
        const taskData = {
            title,
            description,
            priority,
            status,
            dueDate: dueDate || null,
            estimatedMinutes: estimatedMinutes ? parseInt(estimatedMinutes) : null,
            projectId: projectId || null,
            phaseId: phaseId || null,
            tags
        };

        if (onSave) {
            onSave(taskData);
        } else {
            if (task?.id) {
                const wasNotDone = task.status !== 'done';
                const isNowDone = status === 'done';
                const wasDone = task.status === 'done';
                const isNowNotDone = status !== 'done';

                if (wasNotDone && isNowDone) {
                    // Route through completeTask for completedAt + recurrence
                    const { status: _s, ...otherData } = taskData;
                    updateTask(task.id, otherData);
                    completeTask(task.id);
                } else if (wasDone && isNowNotDone) {
                    // Clear completedAt when moving away from done
                    updateTask(task.id, { ...taskData, completedAt: null });
                } else {
                    updateTask(task.id, taskData);
                }
            } else {
                addTask(taskData);
            }
            onClose();
        }
    };

    const statusOptions = [
        { value: 'todo', label: 'To Do' },
        { value: 'in-progress', label: 'In Progress' },
        { value: 'done', label: 'Done' },
    ];

    const projectOptions = [
        { value: '', label: 'No Project' },
        ...projects.map(p => ({ value: p.id, label: p.name, icon: <IconRenderer icon={p.icon || p.emoji} size={16} /> })),
    ];

    return (
        <div className="modal-overlay" onClick={() => {
            if ((title.trim() || getPlainText(description).trim()) && !window.confirm("Discard unsaved changes?")) return;
            onClose();
        }}>
            <div className="modal-content task-modal" onClick={e => e.stopPropagation()} onKeyDown={(e) => {
                // Ensure we don't accidentally close/save if typing in the rich text editor
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && (!e.target.isContentEditable || e.target.tagName !== 'DIV')) {
                    e.preventDefault();
                    if (title.trim()) handleSave();
                }
            }}>
                <div className="modal-header">
                    <h2>{task ? 'Edit Task' : 'New Task'}</h2>
                    <button className="btn btn-ghost btn-icon" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Title */}
                    <input
                        className="task-modal__title-input"
                        placeholder="Task title"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        autoFocus
                    />

                    {/* Meta Row */}
                    <div className="task-modal__row">
                        <div style={{ flex: 1 }}>
                            <CustomSelect
                                value={status}
                                onChange={setStatus}
                                options={statusOptions}
                                placeholder="Status"
                                searchable={false}
                            />
                        </div>

                        <div className="priority-picker">
                            {['low', 'medium', 'high'].map(p => (
                                <button
                                    key={p}
                                    className={`priority-btn ${priority === p ? 'priority-btn--active' : ''}`}
                                    style={{ '--p-color': PRIORITY_COLORS[p] }}
                                    onClick={() => setPriority(p)}
                                    type="button"
                                >
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: 'var(--space-md)' }}>
                        <DualModeEditor
                            content={description}
                            onUpdate={setDescription}
                            placeholder="Description / Notes..."
                            className="task-modal__desc-input"
                        />
                    </div>

                    {/* Details Grid */}
                    <div className="task-modal__grid">
                        <div className="form-group">
                            <label><Calendar size={14} /> Due Date</label>
                            <CustomDatePicker value={dueDate} onChange={setDueDate} placeholder="Set due date..." />
                        </div>
                        <div className="form-group">
                            <label><Clock size={14} /> Estimate</label>
                            <CustomDuration value={estimatedMinutes} onChange={setEstimatedMinutes} />
                        </div>
                        <div className="form-group">
                            <label><Flag size={14} /> Project</label>
                            <CustomSelect
                                value={projectId}
                                onChange={setProjectId}
                                options={projectOptions}
                                placeholder="Select project..."
                            />
                        </div>
                        {projectId && phaseOptions.length > 1 && (
                            <div className="form-group">
                                <label>📊 Phase</label>
                                <CustomSelect
                                    value={phaseId}
                                    onChange={setPhaseId}
                                    options={phaseOptions}
                                    placeholder="Select phase..."
                                    searchable={false}
                                />
                            </div>
                        )}
                    </div>

                    {/* Tags */}
                    <div className="task-modal__tags form-group" style={{ marginTop: 'var(--space-md)' }}>
                        <label><Tag size={14} /> Tags</label>
                        <TagSelector tags={tags} onChange={setTags} />
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={!title.trim()}>
                        {task ? 'Save Changes' : 'Create Task'}
                    </button>
                </div>
            </div>
        </div>
    );
}
